import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';
// @ts-expect-error
import type {MenuListComponentProps, OptionProps} from 'react-select';
// @ts-expect-error
import {components} from 'react-select';

import {colors} from '../colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {CheckmarkIcon} from '../icon/svg/checkmark-icon';
import {WarningIcon} from '../icon/svg/warning-icon';
import {Illustration} from '../illustrations/illustration';
import {Link} from '../link/link';
import {SelectPickerGeneric} from '../select-picker-generic/select-picker-generic';
import type {NoOptionsMessageProps} from '../select-picker-generic/select-picker-generic';

import styles from './subdomain-select.module.css';

const SUBDOMAIN_SELECT_ID = 'subdomain-select';
const DOMAIN_PATH = '/setup/domains';

export type SubdomainOption = {
    value: string;
    label: string;
    customSublabel?: string | React.ReactNode;
    isVerified: boolean;
};

export type Props = {
    selectedOption: SubdomainOption | null | undefined;
    onSelectOption: (option?: SubdomainOption | null | undefined) => void;
    options: SubdomainOption[] | null | undefined;
    // Optional label that appears above the select
    label?: string | React.ReactNode;
    // Whether select has clearable option which shows the 'X' and allows no option to be selected
    isClearable?: boolean;
    // Displays help text below the select
    helpText?: string | React.ReactNode;
    // Displays warning below the select in place of helpText when an unverified option is currently selected
    showUnverifiedHelpText?: boolean;
    // Whether to show 'Add new domain' as the first option in the dropdown
    withCreateNewOption?: boolean;
    // Whether the placeholder should reflect we're selecting a subdomain (vs domain)
    withSubdomainPlaceholder?: boolean;
    // Whether to disable unverified options from being selected
    disableUnverifiedOptions?: boolean;
    // Whether to disable the select
    isDisabled?: boolean;
    noBorder?: boolean;
    customNoOptionsMessage?: NoOptionsMessageProps;
    canAccessSetup?: boolean;
};

export function SubdomainSelect(props: Props) {
    const placeholderDomain = props.withSubdomainPlaceholder ? 'subdomain' : 'domain';
    const placeholder = props.withCreateNewOption
        ? `Select or add a ${placeholderDomain}`
        : `Select a ${placeholderDomain}`;

    return (
        <div className={styles['subdomain-select-wrapper']}>
            {props.label && (
                <label htmlFor={SUBDOMAIN_SELECT_ID} className={styles['select-label']}>
                    {props.label}
                </label>
            )}
            <SelectPickerGeneric
                id={SUBDOMAIN_SELECT_ID}
                options={props.options}
                placeholder={placeholder}
                value={props.selectedOption}
                onChange={(value?: SubdomainOption | null) => props.onSelectOption(value)}
                components={{
                    Option,
                    MenuList,
                }}
                isSearchable={false}
                isLoading={!props.options}
                isDisabled={props.isDisabled}
                onClear={props.isClearable ? () => props.onSelectOption(null) : undefined}
                minHeight={34}
                // @ts-expect-error
                isOptionDisabled={(option) =>
                    Boolean(props.disableUnverifiedOptions && !option.isVerified)
                }
                withCreateNewOption={props.withCreateNewOption}
                customNoOptionsMessage={
                    props.customNoOptionsMessage ||
                    getDefaultCustomNoOptionsMessage(props.canAccessSetup)
                }
                noBorder={props.noBorder}
            />
            {props.selectedOption &&
            !props.selectedOption.isVerified &&
            props.showUnverifiedHelpText ? (
                <div className={styles['unverified-warning-text']}>
                    {/* @ts-expect-error */}
                    <WarningIcon size={15} wrapWithDiv={false} color={colors.orangeAttention} />
                    <div>
                        This still needs to be verified in ‘
                        <Link
                            // @ts-expect-error
                            as={RoutedLink}
                            to={DOMAIN_PATH}
                            newTab={true}
                            size='small'
                            variant='primary'
                        >
                            Domains
                        </Link>
                        ’
                    </div>
                </div>
            ) : (
                props.helpText
            )}
        </div>
    );
}

const Option = (props: OptionProps) => {
    let sublabel;
    if (props.data.customSublabel) {
        sublabel = props.data.customSublabel;
    } else if (props.data.isVerified) {
        sublabel = (
            <>
                <CheckmarkIcon size={11} wrapWithDiv={false} fill={colors.green} />
                <div>Verified</div>
            </>
        );
    } else {
        sublabel = (
            <>
                {/* @ts-expect-error */}
                <WarningIcon size={11} wrapWithDiv={false} fill={colors.orangeAttention} />
                <div>Not verified</div>
            </>
        );
    }

    return (
        <components.Option {...props}>
            <div
                className={
                    props.isDisabled
                        ? styles['subdomain-option--disabled']
                        : styles['subdomain-option']
                }
            >
                <div>{props.data.label}</div>
                <div className={styles['subdomain-option-sublabel']}>{sublabel}</div>
            </div>
        </components.Option>
    );
};

const MenuList = ({children, ...props}: MenuListComponentProps) => (
    <components.MenuList {...props} maxHeight={500}>
        {props.selectProps.options.length &&
        props.selectProps.withCreateNewOption &&
        props.selectProps.canAccessSetup ? (
            <div className={styles['add-new-option']}>
                {/* @ts-expect-error */}
                <Link as={RoutedLink} to={DOMAIN_PATH} newTab={true} size='small' variant='primary'>
                    + Add new domain
                </Link>
            </div>
        ) : null}
        {children}
    </components.MenuList>
);

function getDefaultCustomNoOptionsMessage(canAccessSetup?: boolean) {
    if (canAccessSetup) {
        return DEFAULT_ADMIN_NO_OPTIONS_MESSAGE;
    }

    return DEFAULT_NON_ADMIN_NO_OPTIONS_MESSAGE;
}

export const DEFAULT_ADMIN_NO_OPTIONS_MESSAGE = {
    icon: <Illustration src='www-lady' width={150} />,
    text: (
        <>
            You haven’t added any domains. Once you do and{' '}
            <Link
                href='https://support.nutshell.com/en/articles/8428873-campaigns-how-to-set-up-dkim-spf-and-dmarc'
                newTab={true}
                size='small'
                variant='primary'
            >
                setup DNS records
            </Link>
            , it will appear here.
        </>
    ),
    cta: (
        // @ts-expect-error
        <Link as={RoutedLink} to={DOMAIN_PATH} newTab={true} size='big' variant='button-primary'>
            Add new domain
        </Link>
    ),
} as const;

const DEFAULT_NON_ADMIN_NO_OPTIONS_MESSAGE = {
    icon: <Illustration src='www-lady' width={150} />,
    text: (
        <>
            Your admin hasn’t added any domains. Once they do and{' '}
            <Link
                href='https://support.nutshell.com/en/articles/8428873-campaigns-how-to-set-up-dkim-spf-and-dmarc'
                newTab={true}
                size='small'
                variant='primary'
            >
                setup DNS records
            </Link>
            , it will appear here.
        </>
    ),
    cta: null,
} as const;
